<template>
  <v-container class="px-0 py-0" fluid>
    <v-row dense>
      <v-col>
        <v-skeleton-loader v-if="isProcessingComputed" type="table" />
        <v-card v-else outlined>
          <v-card-title class="pt-1 pb-1">
            <v-col cols="4">
              <TextFieldRef :label="$t('ELE0032')" :value="tableTitleName" />
            </v-col>
            <v-col cols="2">
              <TextFieldRef
                v-if="inspCharacTab == enumInspCharacTabs.IMPORT"
                :value="currentRev"
                :label="$t('ELE0194')"
                disabled
              />
              <SelectSingleInput
                v-if="inspCharacTab == enumInspCharacTabs.SEARCH"
                v-model="revValue"
                :items="revNos"
                :label="$t('ELE0102')"
                item-text="number"
                item-value="number"
                :clearable="false"
                @change="onChangeRev"
              />
            </v-col>
            <div>
              {{ '->' }}
            </div>
            <v-col cols="1">
              <TextFieldRef v-if="currentRev >= 1" :value="currentRev + 1" :label="$t('ELE0195')" disabled />
              <TextFieldRef v-else :value="$t('ELE0123')" :label="$t('ELE0195')" disabled />
            </v-col>
            <v-spacer />
            <Btn class="mx-2" :label="$t('ELE0101')" @click="editMultipleItems" :disabled="!isSelected" />
            <BtnCsvDownload class="mx-2" :label="$t('ELE0040')" fileTitle="sparq_output_data" :items="csvOutputData" />
            <BtnAdd @click="addItem" />
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <v-data-table
                  :headers="headers"
                  :items="items"
                  fixed-header
                  item-key="class"
                  disable-pagination
                  hide-default-footer
                  :height="tableHeight"
                  dense
                >
                  <template v-slot:item="{ item, index }">
                    <tr @click="onClickTableRow(index)" :class="item.errorExists ? 'cell-error' : ''">
                      <td class="mx-0 px-0" :style="showBorderBottom(index)">
                        <v-simple-checkbox label="" v-model="item.check" :ripple="false" color="primary" />
                        <v-icon color="primary" @click="onClickUp(item, index)"> mdi-arrow-up-bold </v-icon>
                        <v-icon color="primary" @click="onClickDown(item, index)"> mdi-arrow-down-bold </v-icon>
                      </td>
                      <td class="v-data-table__divider mx-0 px-0" :style="showBorderBottom(index)">
                        <v-icon color="primary" @click="deleteItem(index)" left>mdi-delete</v-icon>
                        <IconEdit @click="editItem(item, index)" right />
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">
                        {{ item.inspCharacCd }}
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">
                        {{ item.inspCharacPoint }}
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">
                        <div v-if="item.measFmtCd == emumMeasFmtCd.NUMBER || item.measFmtCd == emumMeasFmtCd.OK_NG">
                          {{ item.measFmtCd + ' : ' + $t('measFmtCd.' + item.measFmtCd) }}
                        </div>
                        <div v-else>{{ item.measFmtCd }}</div>
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">
                        <div v-if="item.inspCharacCatCd">
                          {{ item.inspCharacCatCd + ' : ' + $t('InspCharacCatCd.' + item.inspCharacCatCd) }}
                        </div>
                        <div v-else>{{ '' }}</div>
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">{{ item.inspGradeCd }}</td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">{{ item.measInstCd }}</td>
                      <td class="text-right v-data-table__divider" :style="showBorderBottom(index)">
                        {{ item.standard }}
                      </td>
                      <td class="text-right v-data-table__divider" :style="showBorderBottom(index)">
                        {{ item.upper }}
                      </td>
                      <td class="text-right v-data-table__divider" :style="showBorderBottom(index)">
                        {{ item.lower }}
                      </td>
                      <td class="text-right v-data-table__divider" :style="showBorderBottom(index)">
                        {{ item.clUpper }}
                      </td>
                      <td class="text-right v-data-table__divider" :style="showBorderBottom(index)">
                        {{ item.clLower }}
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">{{ item.remarks }}</td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">
                        {{ getInspStd(item.inspStdCd) }}
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">
                        {{ getQltyStd(item.qltyStdCd1) }}
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">
                        {{ getQltyStd(item.qltyStdCd2) }}
                      </td>
                      <td class="v-data-table__divider" :style="showBorderBottom(index)">
                        {{ item.qltyStd3 }}
                      </td>
                      <td :style="showBorderBottom(index)">
                        <div style="white-space: pre">{{ item.error }}</div>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-divider />
            <validation-observer ref="observer">
              <v-row class="mt-2">
                <v-col cols="4">
                  <TextFieldInput
                    :label="$t('ELE0089')"
                    :maxLength="tecNoLimit"
                    v-model="hdrItems.tecNo"
                    :required="tecNoReq"
                  />
                </v-col>
                <v-col cols="5">
                  <TextFieldInput :label="$t('ELE0090')" :maxLength="1000" v-model="hdrItems.reason" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="4">
                  <TextFieldRef
                    v-if="inspCharacTab == enumInspCharacTabs.SEARCH"
                    :label="$t('ELE0030')"
                    v-model="hdrItems.updatedAt"
                  />
                </v-col>
                <v-col cols="4">
                  <TextFieldRef
                    v-if="inspCharacTab == enumInspCharacTabs.SEARCH"
                    :label="$t('ELE0039')"
                    v-model="hdrItems.updaterBy"
                  />
                </v-col>
              </v-row>
            </validation-observer>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <InspCharacDialog
      v-if="item !== null"
      :dialog.sync="dialog"
      :mode="mode"
      :partCd="partCd"
      :item.sync="item"
      @addItem="addItemProcess"
      @setItem="editItemProcess"
      @editMultipleItems="editMultipleItemsProcess"
    />
  </v-container>
</template>

<script>
import { API } from '@/plugins/api'

import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils'
import StringUtils from '@/mixin/string-utils'
import { ModalMode, InspCharacCatCd, MeasFmtCd, InspGradeCd } from '@/enum'

import { ValidationObserver } from 'vee-validate'
import Btn from '@/components/atoms/base/button/Btn.vue'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import IconEdit from '@/components/atoms/icon/IconEdit'
import BtnAdd from '@/components/atoms/button/BtnAdd'
import BtnReset from '@/components/atoms/button/BtnReset'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import InspCharacDialog from '@/components/organisms/master/dialog/InspCharacDialog'
import cloneDeep from 'lodash/cloneDeep'
import BtnCsvDownload from '@/components/atoms/button/BtnCsvDownload'
import InspCharacTable from '@/components/organisms/master/form/InspCharacTable'

/** 特性マスタ取込 CSV取込モード **/
const ImportInspCharMode = {
  CAD: '1',
  SPARQ: '2',
}
/** 特性マスタ取込 タブ **/
const InspCharacTabs = {
  IMPORT: 0,
  SEARCH: 1,
}
/** 処理用 **/
const SaveStatus = {
  SAVING: '1',
  SUCCESS: '2',
  ERROR: '3',
}

export default {
  name: 'InspCharacTable',

  mixins: [APIUtils, Message, Loading, DateUtils, StringUtils],

  components: {
    ValidationObserver,
    Btn,
    SelectSingleInput,
    IconEdit,
    BtnAdd,
    TextFieldInput,
    TextFieldRef,
    BtnReset,
    InspCharacDialog,
    BtnCsvDownload,
    InspCharacTable,
  },

  props: {
    file: {
      type: File,
      required: false,
      default: null,
    },
    tabTitle: {
      type: String,
      required: true,
      default: '',
    },
    searchPartCd: {
      type: String,
      required: false,
      default: '',
    },
    csvType: {
      type: String,
      required: true,
      default: '',
    },
    isProcessing: {
      type: Boolean,
      required: true,
      default: false,
    },
    inspCharacTab: {
      type: Number,
      required: true,
      default: 0,
    },
    saveStatus: {
      type: String,
      required: true,
      default: '',
    },
    inspStd: {
      type: Array,
      required: true,
      default: () => [],
    },
    qltyStd: {
      type: Array,
      required: true,
      default: () => [],
    },
    tecNoLimit: {
      type: Number,
      required: true,
      default: 200,
    },
    tecNoReq: {
      type: Boolean,
      required: true,
      default: false,
    },
    ptCdLen: {
      type: String,
      required: true,
      default: '',
    },
  },

  data() {
    return {
      dialog: false,
      isDisabledTabItems: false,
      mode: '',
      item: null,
      revValue: '',
      inspHdr: [],
      revNos: [],
      clickedRowIndex: null,
      currentRev: '',
      items: [],
      partCd: '',
      tableTitleName: '',
      editIndex: '',
      editIndexes: [],
      headers: [
        { text: '', value: 'check', sortable: false },
        { text: '', width: '75', value: 'action', divider: true, class: 'ml-0 pl-0', sortable: false },
        { text: this.$t('ELE0091'), value: 'inspCharacCd', divider: true, sortable: false },
        { text: this.$t('ELE0092'), value: 'inspCharacPoint', divider: true, sortable: false },
        { text: this.$t('ELE0093'), value: 'measFmtCd', divider: true, sortable: false },
        { text: this.$t('ELE0293'), value: 'inspCharacCatCd', divider: true, sortable: false },
        { text: this.$t('ELE0094'), value: 'inspGradeCd', divider: true, sortable: false },
        { text: this.$t('ELE0025'), value: 'measInstCd', divider: true, sortable: false },
        { text: this.$t('ELE0095'), value: 'standard', divider: true, align: 'end', sortable: false },
        { text: this.$t('ELE0096'), value: 'upper', divider: true, align: 'end', sortable: false },
        { text: this.$t('ELE0097'), value: 'lower', divider: true, align: 'end', sortable: false },
        { text: this.$t('ELE0538'), value: 'clUpper', divider: true, align: 'end', sortable: false },
        { text: this.$t('ELE0539'), value: 'clLower', divider: true, align: 'end', sortable: false },
        { text: this.$t('ELE0099'), value: 'remarks', divider: true, sortable: false },
        { text: this.$t('ELE0388'), value: 'inspStdCd', divider: true, sortable: false },
        { text: this.$t('ELE0389'), value: 'qltyStdCd1', divider: true, sortable: false },
        { text: this.$t('ELE0390'), value: 'qltyStdCd2', divider: true, sortable: false },
        { text: this.$t('ELE0391'), value: 'qltyStd3', divider: true, sortable: false },
        { text: this.$t('ELE0100'), value: 'error', sortable: false },
      ],
      emptyItems: {
        partCd: '',
        inspCharacCd: '',
        inspCharacPoint: '',
        measFmtCd: '',
        inspGradeCd: '',
        measInstCd: '',
        standard: '',
        upper: '',
        lower: '',
        clUpper: '',
        clLower: '',
        remarks: '',
        inspStdCd: '',
        qltyStdCd1: '',
        qltyStdCd2: '',
        qltyStd3: '',
        check: false,
        tecNo: '',
        reason: '',
      },
      hdrItems: {
        tecNo: '',
        reason: '',
        updatedAt: '',
        updaterBy: '',
      },
    }
  },

  watch: {
    file: {
      handler(value) {
        if (value !== null) {
          this.import(value)
        }
      },
      immediate: true,
    },
    searchPartCd: {
      handler(value) {
        if (value != '') {
          this.onClickSearch()
        }
      },
      immediate: true,
    },
  },

  computed: {
    enumImportInspCharMode: () => ImportInspCharMode,
    emumMeasFmtCd: () => MeasFmtCd,
    enumInspCharacTabs: () => InspCharacTabs,
    enumSaveStatus: () => SaveStatus,
    tabTitleComputed: {
      get() {
        return this.tabTitle
      },
      set(value) {
        this.$emit('update:tabTitle', value)
      },
    },
    tableHeight: {
      get() {
        let height = this.$vuetify.breakpoint.height * 0.5
        if (height < 360) {
          height = 360
        }
        return `${height}px`
      },
    },
    isSelected: {
      get() {
        return this.items.some((e) => e.check)
      },
    },
    csvOutputData: {
      get() {
        let e = this.items
        let items = []
        for (let index = 0; index <= e.length - 1; index++) {
          let item = {}
          item[this.$t('ELE0193')] = this.partCd
          item[this.$t('ELE0089')] = ''
          item[this.$t('ELE0091')] = e[index].inspCharacCd
          item[this.$t('ELE0092')] = e[index].inspCharacPoint
          item[this.$t('ELE0093')] = e[index].measFmtCd
          item[this.$t('ELE0094')] = e[index].inspGradeCd
          item[this.$t('ELE0025')] = e[index].measInstCd
          item[this.$t('ELE0095')] = e[index].standard
          item[this.$t('ELE0096')] = e[index].upper
          item[this.$t('ELE0097')] = e[index].lower
          item[this.$t('ELE0538')] = e[index].clUpper
          item[this.$t('ELE0539')] = e[index].clLower
          item[this.$t('ELE0099')] = e[index].remarks
          item[this.$t('ELE0388')] = e[index].inspStdCd
          item[this.$t('ELE0389')] = e[index].qltyStdCd1
          item[this.$t('ELE0390')] = e[index].qltyStdCd2
          item[this.$t('ELE0391')] = e[index].qltyStd3
          item[this.$t('ELE0090')] = ''
          items.push(item)
        }
        if (this.items.length <= 0) {
          let item = {}
          item[this.$t('ELE0193')] = ''
          item[this.$t('ELE0089')] = ''
          item[this.$t('ELE0091')] = ''
          item[this.$t('ELE0092')] = ''
          item[this.$t('ELE0093')] = ''
          item[this.$t('ELE0094')] = ''
          item[this.$t('ELE0025')] = ''
          item[this.$t('ELE0095')] = ''
          item[this.$t('ELE0096')] = ''
          item[this.$t('ELE0097')] = ''
          item[this.$t('ELE0538')] = ''
          item[this.$t('ELE0539')] = ''
          item[this.$t('ELE0099')] = ''
          item[this.$t('ELE0388')] = ''
          item[this.$t('ELE0389')] = ''
          item[this.$t('ELE0390')] = ''
          item[this.$t('ELE0391')] = ''
          item[this.$t('ELE0090')] = ''
          items.push(item)
        }
        items[0][this.$t('ELE0089')] = this.hdrItems.tecNo
        items[0][this.$t('ELE0090')] = this.hdrItems.reason
        return items
      },
    },
    isProcessingComputed: {
      get() {
        return this.isProcessing
      },
      set(value) {
        this.$emit('update:isProcessing', value)
      },
    },
    saveStatusComputed: {
      get() {
        return this.saveStatus
      },
      set(value) {
        this.$emit('update:saveStatus', value)
      },
    },
  },

  methods: {
    async onClickSearch() {
      this.isProcessingComputed = true
      this.isDisabledTabItems = true
      try {
        const elements = await API.get('/master/inspCharacHdrs/' + this.searchPartCd)
        this.tabTitleComputed = this.searchPartCd
        this.partCd = this.searchPartCd
        this.tableTitleName = elements.partName
        this.revNos = []
        this.items = []
        if (elements.exists) {
          this.inspHdr = elements.inspHdrList
          this.revValue = String(elements.inspHdrList[0].inspCharacRev)
          this.revNos = elements.inspHdrList.map((inspHdr) => {
            return { number: String(inspHdr.inspCharacRev) }
          })
          this.items = elements.inspDtlList
          this.hdrItems.tecNo = elements.inspHdrList[0].tecNo
          this.hdrItems.reason = elements.inspHdrList[0].reason
          this.hdrItems.updatedAt = this.toLocalTime(elements.inspHdrList[0].itemUpdatedAt)
          this.hdrItems.updaterBy = elements.inspHdrList[0].m_user.userName
          this.currentRev = elements.inspCharacLatestRev
        } else {
          // Revを1にして空のResultを表示させる
          this.revValue = elements.inspCharacLatestRev
          this.revNos = [{ number: elements.inspCharacLatestRev }]
          this.items = []
          this.currentRev = elements.inspCharacLatestRev
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0271') })))
      } finally {
        this.isProcessingComputed = false
      }
    },
    async onChangeRev() {
      try {
        this.isProcessingComputed = true
        const body = {
          partCd: this.searchPartCd,
          revNo: this.revValue,
        }
        this.items = await API.get('/master/inspCharacDtls', body)
        this.hdrItems.tecNo = this.inspHdr.find((e) => e.inspCharacRev == body.revNo).tecNo
        this.hdrItems.reason = this.inspHdr.find((e) => e.inspCharacRev == body.revNo).reason

        this.hdrItems.updatedAt = this.toLocalTime(
          this.inspHdr.find((e) => e.inspCharacRev == body.revNo).itemUpdatedAt,
        )
        this.hdrItems.updaterBy = this.inspHdr.find((e) => e.inspCharacRev == body.revNo).m_user.userName
      } finally {
        this.isProcessingComputed = false
      }
    },
    import(file) {
      if (this.csvType == ImportInspCharMode.CAD) {
        this.onClickImportCAD(file)
      } else {
        this.onClickImportSPARQ(file)
      }
    },
    onClickImportCAD(file) {
      this.isDisabledTabItems = true
      this.items = []
      this.csvToJson(file, this.checkImportCAD)
    },
    onClickImportSPARQ(file) {
      this.isDisabledTabItems = true
      this.items = []
      this.csvToJson(file, this.checkImportSPARQ)
    },
    onClickUp(item, index) {
      if (index != 0) {
        this.items.splice(index, 1)
        this.items.splice(index - 1, 0, item)
      }
    },
    onClickDown(item, index) {
      if (index != this.items.length - 1) {
        this.items.splice(index, 1)
        this.items.splice(index + 1, 0, item)
      }
    },
    onClickTableRow(row) {
      this.clickedRowIndex = row
    },
    csvToJson(file, callback) {
      this.$papa.parse(file, {
        skipEmptyLines: true,
        complete: function (results) {
          callback(results.data)
        },
      })
    },
    addItem() {
      this.mode = ModalMode.ADD
      this.item = cloneDeep(this.emptyItems)
      this.item.partCd = this.partCd
      this.dialog = true
    },
    editItem(item, index) {
      this.mode = ModalMode.EDIT
      this.item = cloneDeep(item)
      this.dialog = true
      this.editIndex = index
    },
    deleteItem(index) {
      this.items.splice(index, 1)
    },
    editMultipleItems() {
      this.mode = ModalMode.MULTIPLE
      this.editIndexes = this.items
        .map((e, i) => {
          // check==trueの場合にindex番号、違う場合は-1の配列を作成
          return e.check === true ? i : -1
        })
        .filter((e) => {
          // index番号が入った配列を返す
          return e !== -1
        })
      this.item = cloneDeep(this.items[this.editIndexes[0]])
      this.dialog = true
    },
    addItemProcess() {
      this.item.inspCharacCatCd = this.judgeInspCharacCatCd(this.item.standard, this.item.upper, this.item.lower)
      this.items.splice(this.clickedRowIndex + 1, 0, this.item)
    },
    editItemProcess() {
      this.item.inspCharacCatCd = this.judgeInspCharacCatCd(this.item.standard, this.item.upper, this.item.lower)
      this.items.splice(this.editIndex, 1, this.item)
    },
    editMultipleItemsProcess() {
      this.editIndexes.forEach((i) => {
        this.items[i].check = false
        this.items[i].measFmtCd = this.item.measFmtCd
        this.items[i].inspGradeCd = this.item.inspGradeCd
        this.items[i].measInstCd = this.item.measInstCd
        this.items[i].standard = this.item.standard
        this.items[i].upper = this.item.upper
        this.items[i].lower = this.item.lower
        this.items[i].clUpper = this.item.clUpper
        this.items[i].clLower = this.item.clLower
        this.items[i].inspCharacCatCd = this.judgeInspCharacCatCd(this.item.standard, this.item.upper, this.item.lower)
        this.items[i].remarks = this.item.remarks
        this.items[i].inspStdCd = this.item.inspStdCd
        this.items[i].qltyStdCd1 = this.item.qltyStdCd1
        this.items[i].qltyStdCd2 = this.item.qltyStdCd2
        this.items[i].qltyStd3 = this.item.qltyStd3
      })
    },

    async checkImportCAD(csvData) {
      try {
        this.isProcessingComputed = true
        const items = await this.setCadItems(csvData)
        if (!items) {
          // falseが帰ってきた場合エラー
          this.addErrorMessage(`${this.$t('MSG0028')} (${this.file.name})`)
          this.$emit('deleteTab')
          return
        }
        const ptCd = await this.generatePtCd(items[0].partCd)
        const body = {
          ptCd: ptCd,
          items: items,
        }

        const elements = await API.post('/master/inspChars/checkCadItems', body)
        this.tabTitleComputed = elements.partCd
        this.partCd = elements.partCd
        this.tableTitleName = elements.partName
        this.currentRev = elements.inspCharacLatestRev
        if (elements.errorExists) {
          this.saveStatusComputed = SaveStatus.ERROR
        }
        elements.errorMessages.forEach((element, index) => {
          let item = {}
          item.inspCharacCd = items[index].inspCharacCd
          item.inspCharacPoint = items[index].inspCharacPoint
          item.measFmtCd = items[index].measFmtCd
          item.inspCharacCatCd = items[index].inspCharacCatCd
          item.inspGradeCd = items[index].inspGradeCd
          item.measInstCd = items[index].measInstCd
          item.standard = items[index].standard
          item.upper = items[index].upper
          item.lower = items[index].lower
          item.clUpper = items[index].clUpper
          item.clLower = items[index].clLower
          item.remarks = ''
          item.inspStdCd = items[index].inspStdCd
          item.qltyStdCd1 = items[index].qltyStdCd1
          item.qltyStdCd2 = items[index].qltyStdCd2
          item.qltyStd3 = items[index].qltyStd3
          item.check = false
          item.error = element.bodies.join('\r\n')
          item.errorExists = element.bodies.length ? true : false
          this.items.push(item)
        })
        this.hdrItems.tecNo = ''
        this.hdrItems.reason = ''
      } catch (error) {
        const msg = this.resolveErrorMessage(error, this.$t('MSG0005', { p0: this.$t('ELE0271') }))
        this.addErrorMessage(msg + ` (${this.file.name})`)
        this.$emit('deleteTab')
      } finally {
        this.isProcessingComputed = false
      }
    },
    async checkImportSPARQ(csvData) {
      try {
        this.isProcessingComputed = true
        const items = await this.setSparqItems(csvData)
        if (!items) {
          // falseが帰ってきた場合エラー
          this.addErrorMessage(`${this.$t('MSG0028')} (${this.file.name})`)
          this.$emit('deleteTab')
          return
        }
        const ptCd = await this.generatePtCd(items[0].partCd)
        const body = {
          ptCd: ptCd,
          items: items,
        }

        const elements = await API.post('/master/inspChars/checkSparqItems', body)
        this.tabTitleComputed = elements.partCd
        this.partCd = elements.partCd
        this.tableTitleName = elements.partName
        this.currentRev = elements.inspCharacLatestRev
        if (elements.errorExists) {
          this.saveStatusComputed = SaveStatus.ERROR
        }
        elements.errorMessages.forEach((element, index) => {
          let item = {}
          item.inspCharacCd = items[index].inspCharacCd
          item.inspCharacPoint = items[index].inspCharacPoint
          item.measFmtCd = items[index].measFmtCd
          item.inspCharacCatCd = items[index].inspCharacCatCd
          item.inspGradeCd = items[index].inspGradeCd
          item.measInstCd = items[index].measInstCd
          item.standard = items[index].standard
          item.upper = items[index].upper
          item.lower = items[index].lower
          item.clUpper = items[index].clUpper
          item.clLower = items[index].clLower
          item.remarks = items[index].remarks
          item.inspStdCd = items[index].inspStdCd
          item.qltyStdCd1 = items[index].qltyStdCd1
          item.qltyStdCd2 = items[index].qltyStdCd2
          item.qltyStd3 = items[index].qltyStd3
          item.check = false
          item.error = element.bodies.join('\r\n')
          item.errorExists = element.bodies.length ? true : false
          this.items.push(item)
        })
        this.hdrItems.tecNo = items[0].tecNo
        this.hdrItems.reason = items[0].reason
      } catch (error) {
        const msg = this.resolveErrorMessage(error, this.$t('MSG0005', { p0: this.$t('ELE0271') }))
        this.addErrorMessage(msg + ` (${this.file.name})`)
        this.$emit('deleteTab')
      } finally {
        this.isProcessingComputed = false
      }
    },
    async setCadItems(csvData) {
      if (Object.keys(csvData[0]).length != 12) return false
      if (csvData.length <= 1) return []
      const revNo = await this.getCharacRev(csvData[1][0])
      let items = []
      let index = 1
      for (let i = 1; i <= csvData.length - 1; i++) {
        for (let j = 0; j < csvData[i][1]; j++) {
          // CSVの個数カラムの値分ループする
          if (csvData[i][5] == '') {
            continue
          }
          let item = {}
          item.partCd = csvData[i][0]
          item.inspCharacRev = revNo
          item.inspCharacDtlNo = index
          // 取込後、Backendでチェックするのでtrim追加
          item.inspCharacCd = csvData[i][5].trim()
          if (csvData[i][1] == '1') {
            item.inspCharacPoint = ''
          } else {
            item.inspCharacPoint = String(j + 1)
          }
          item.inspCharacCatCd = this.judgeInspCharacCatCd(csvData[i][2], csvData[i][3], csvData[i][4])
          item.measFmtCd = this.judgeMeasFmtCd(csvData[i][2], csvData[i][3], csvData[i][4])
          item.inspGradeCd = this.judgeGradeCd(csvData[i][5])
          item.measInstCd = csvData[i][6]
          item.standard = csvData[i][2]
          item.upper = csvData[i][3]
          item.lower = csvData[i][4]
          item.clUpper = ''
          item.clLower = ''
          item.remarks = ''
          item.inspStdCd = ''
          item.qltyStdCd1 = ''
          item.qltyStdCd2 = ''
          item.qltyStd3 = ''
          item.tecNo = ''
          item.reason = ''
          items.push(item)
          index += 1
        }
      }
      return items
    },
    setSparqItems(csvData) {
      let items = []
      if (Object.keys(csvData[0]).length != 18) return false
      for (let i = 1; i <= csvData.length - 1; i++) {
        let item = {}
        item.partCd = csvData[i][0]
        item.tecNo = csvData[1][1]
        // 取込後、Backendでチェックするのでtrim追加
        item.inspCharacCd = csvData[i][2].trim()
        item.inspCharacPoint = csvData[i][3].trim()
        item.inspCharacCatCd = this.judgeInspCharacCatCd(csvData[i][7], csvData[i][8], csvData[i][9])
        item.measFmtCd = this.judgeMeasFmtCd(csvData[i][7], csvData[i][8], csvData[i][9])
        item.inspGradeCd = csvData[i][5]
        item.measInstCd = csvData[i][6]
        item.standard = csvData[i][7]
        item.upper = csvData[i][8]
        item.lower = csvData[i][9]
        item.clUpper = csvData[i][10]
        item.clLower = csvData[i][11]
        item.remarks = csvData[i][12]
        item.inspStdCd = csvData[i][13]
        item.qltyStdCd1 = csvData[i][14]
        item.qltyStdCd2 = csvData[i][15]
        item.qltyStd3 = csvData[i][16]
        item.reason = csvData[1][17]
        items.push(item)
      }
      return items
    },
    setSaveItems(e) {
      let items = []
      for (let index = 0; index <= e.length - 1; index++) {
        let item = {
          partCd: this.partCd,
          tecNo: this.hdrItems.tecNo,
          inspCharacCd: e[index].inspCharacCd ? e[index].inspCharacCd.trim() : e[index].inspCharacCd,
          inspCharacPoint: e[index].inspCharacPoint ? e[index].inspCharacPoint.trim() : e[index].inspCharacPoint,
          inspCharacCatCd: e[index].inspCharacCatCd,
          measFmtCd: e[index].measFmtCd,
          inspGradeCd: e[index].inspGradeCd,
          measInstCd: e[index].measInstCd,
          standard: e[index].standard === '' ? null : e[index].standard,
          upper: e[index].upper === '' ? null : e[index].upper,
          lower: e[index].lower === '' ? null : e[index].lower,
          clUpper: e[index].clUpper === '' ? null : e[index].clUpper,
          clLower: e[index].clLower === '' ? null : e[index].clLower,
          remarks: e[index].remarks,
          inspStdCd: e[index].inspStdCd === '' ? null : e[index].inspStdCd,
          qltyStdCd1: e[index].qltyStdCd1 === '' ? null : e[index].qltyStdCd1,
          qltyStdCd2: e[index].qltyStdCd2 === '' ? null : e[index].qltyStdCd2,
          qltyStd3: e[index].qltyStd3 === '' ? null : e[index].qltyStd3,
          reason: this.hdrItems.reason,
        }
        items.push(item)
      }
      return items
    },

    async onClickSave() {
      if (this.saveStatusComputed == SaveStatus.SUCCESS) {
        // 既に保存が完了している場合、保存ロジックを走らせない
        return
      }
      if (!(await this.$refs.observer.validate())) {
        this.saveStatusComputed = SaveStatus.ERROR
        return
      }
      try {
        this.isProcessingComputed = true
        this.saveStatusComputed = SaveStatus.SAVING
        const items = await this.setSaveItems(this.items)
        const ptCd = await this.generatePtCd(items[0].partCd)
        const body = {
          ptCd: ptCd,
          items: items,
          revNo: this.currentRev + 1,
        }
        const elements = await API.post('/master/inspCharacs/checkItems', body)
        if (elements.errorExists) {
          this.items = []
          elements.errorMessages.forEach((element, index) => {
            let item = {}
            item.inspCharacCd = items[index].inspCharacCd
            item.inspCharacPoint = items[index].inspCharacPoint
            item.inspCharacCatCd = items[index].inspCharacCatCd
            item.measFmtCd = items[index].measFmtCd
            item.inspGradeCd = items[index].inspGradeCd
            item.measInstCd = items[index].measInstCd
            item.standard = items[index].standard
            item.upper = items[index].upper
            item.lower = items[index].lower
            item.clUpper = items[index].clUpper
            item.clLower = items[index].clLower
            item.remarks = items[index].remarks
            item.inspStdCd = items[index].inspStdCd
            item.qltyStdCd1 = items[index].qltyStdCd1
            item.qltyStdCd2 = items[index].qltyStdCd2
            item.qltyStd3 = items[index].qltyStd3
            item.check = false
            item.error = element.bodies.join('\r\n')
            item.errorExists = element.bodies.length ? true : false
            this.items.push(item)
          })
          this.saveStatusComputed = SaveStatus.ERROR
          this.hdrItems.tecNo = items[0].tecNo
          this.hdrItems.reason = items[0].reason
        } else {
          await this.save(body)
          this.saveStatusComputed = SaveStatus.SUCCESS
        }
      } catch (error) {
        this.saveStatusComputed = SaveStatus.ERROR
        const msg = this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0271') }))
        this.addErrorMessage(msg + ` (${this.$t('ELE0059')}: ${this.partCd})`)
      } finally {
        this.isProcessingComputed = false
        this.$emit('finishedSave')
      }
    },
    async save(body) {
      await API.post('/master/inspCharacs/register', body)
      this.items = this.items.map((e) => {
        e.error = ''
        return e
      })
    },

    judgeGradeCd(char) {
      if (char.length == 1) {
        return InspGradeCd.A
      } else if (char.length == 2) {
        return InspGradeCd.B
      } else {
        return ''
      }
    },
    judgeMeasFmtCd(std, upper, lower) {
      let result = MeasFmtCd.NUMBER
      // バックエンドから空のデータを取得するとnullになるので===null追加
      if (this.isEmpty(std) && this.isEmpty(upper) && this.isEmpty(lower)) {
        result = MeasFmtCd.OK_NG
      }
      return result
    },
    judgeInspCharacCatCd(std, upper, lower) {
      let result = ''
      // バックエンドから空のデータを取得するとnullになるので===null追加
      if (this.isEmpty(std) && this.isEmpty(upper) && this.isEmpty(lower)) {
        result = InspCharacCatCd.OK_NG
      } else if (this.isEmpty(std) && !this.isEmpty(upper) && this.isEmpty(lower)) {
        result = InspCharacCatCd.GEOMETRIC_TOLERANCE
      } else if (!this.isEmpty(std)) {
        if (!this.isEmpty(upper) || !this.isEmpty(lower)) {
          result = InspCharacCatCd.DIMENSIONAL_TOLERANCE
        }
      }
      return result
    },
    getInspStd(inspStdCd) {
      return this.inspStd.find((item) => item.inspStdCd == inspStdCd)?.text
    },
    getQltyStd(qltyStdCd) {
      return this.qltyStd.find((item) => item.qltyStdCd == qltyStdCd)?.text
    },

    async getCharacRev(partCd) {
      let result = 1
      try {
        const rev = await API.get('/master/inspCharacHdrs/' + partCd + '/getRev')
        result = rev
      } catch (error) {
        if (error.response.status == 404) {
          result = 1
        } else {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0271') })))
        }
      }
      return result
    },
    showBorderBottom(index) {
      if (this.clickedRowIndex === index) {
        return { borderBottom: 'thick solid #43A047 !important' }
      }
    },
    generatePtCd(partCd) {
      let result = partCd
      if (partCd != '' && partCd != null && partCd.length >= this.ptCdLen) {
        result = partCd.substr(0, this.ptCdLen)
      }
      return result
    },
  },
}
</script>
